import { Capacitor } from "@capacitor/core";

/**
 * The clock, signal and battery are drawn by the OS over whatever we paint,
 * so their colour is ours to set. The app runs edge to edge (native.css), so
 * what they sit on is the screen itself: the cream canvas for most of it, and
 * the leaf, the sign-in art, the door maroon and the room's ink for the
 * screens that fill the strip with their own dark ground. Light icons for
 * those, dark for the canvas.
 *
 * The native launch screen, which is up before any of this can run, is
 * covered by UIStatusBarStyle in ios/App/App/Info.plist instead.
 */

/**
 * Every ground a screen can put behind the status bar, and whether the icons
 * have to go light for it. Each colour is the one that screen is painting up
 * there, so the strip is never a shade of its own: the splash's letterbox
 * green and the app canvas match capacitor.config.ts, and the rest match what
 * app.css and native.css paint into the inset.
 */
const GROUNDS = {
  /** Leaf green: SplashScreen.backgroundColor in capacitor.config.ts. */
  splash: { color: "#3E5539", lightIcons: true },
  /** App canvas: ios/android backgroundColor in capacitor.config.ts. */
  app: { color: "#FAF8F4", lightIcons: false },
  /** Sign-in art, at its top edge: the texture under the gradient's first stop. */
  art: { color: "#5F2E31", lightIcons: true },
  /** "at the door": --surface-door, the coral tree darkened. */
  door: { color: "#5C3637", lightIcons: true },
  /** In the room: --surface-ink. */
  room: { color: "#1F2937", lightIcons: true },
} as const;

export type StatusBarGround = keyof typeof GROUNDS;

/** Last request wins, however the plugin's promises happen to settle. */
let latest = 0;

/**
 * The plugin module, fetched at most once.
 *
 * Two requests can overlap — a splash unmounting into another asks for "app"
 * then "splash" in the same tick — and each starting its own dynamic import of
 * the same module buys nothing. Share one promise between them instead.
 */
let pluginPromise: Promise<typeof import("@capacitor/status-bar")> | null =
  null;

function loadPlugin() {
  if (!pluginPromise) {
    pluginPromise = import("@capacitor/status-bar").catch((err) => {
      // Don't cache a failure: a build that syncs the plugin later should get
      // it on the next request rather than be written off for the session.
      pluginPromise = null;
      throw err;
    });
  }
  return pluginPromise;
}

/**
 * Colour the status bar for the ground it is sitting on. No-op on the web,
 * where the browser owns its own chrome. The plugin is imported lazily so the
 * website never pays for it.
 */
export function setNativeStatusBar(ground: StatusBarGround): void {
  let native = false;
  try {
    native = Capacitor.isNativePlatform();
  } catch {
    return;
  }
  if (!native) return;

  const token = ++latest;
  void loadPlugin()
    .then(({ StatusBar, Style }) => {
      if (token !== latest) return undefined;
      const { color, lightIcons } = GROUNDS[ground];
      // Capacitor names these after the BACKGROUND, not the text: Style.Dark
      // is light text for a dark ground, Style.Light is dark text for a light
      // one. Read them as "dark ground" / "light ground".
      return Promise.all([
        StatusBar.setStyle({ style: lightIcons ? Style.Dark : Style.Light }),
        // Android draws a solid strip behind the icons; iOS has no such thing
        // and rejects this, which is why it swallows its own error.
        StatusBar.setBackgroundColor({ color }).catch(() => {}),
      ]);
    })
    .catch(() => {
      // Plugin missing (web build, or a native build that hasn't synced yet)
      // — the status bar keeps whatever the launch screen set.
    });
}

export default setNativeStatusBar;
